import { DD } from '@tcc/analytics';

const APP_TOTAL_LOAD_VITAL = 'app_total_load';

// Backstop: ignore implausibly long "loads" (e.g. a tab left sitting on the
// login form or a backgrounded tab) so they cannot skew the percentiles.
const MAX_PLAUSIBLE_LOAD_MS = 120_000;

// Always-on "shell" plugins (the nav/InfoArchitecture shell and the auth plugin)
// mount early on every load and are not the route's content. They must not close
// the end-to-end timer, otherwise app_total_load measures time-to-shell instead
// of time-to-content.
const SHELL_PLUGINS = new Set(['information-architecture', 'tcc-auth-plugin']);

let hasRecorded = false;
let initialized = false;
// Pessimistic default: assume a manual login until a valid session proves otherwise.
let manualLogin = true;

/**
 * Records whether the app had a valid session at cold load — exactly once per
 * page load. A valid session means the interactive login form was NOT shown, so
 * the end-to-end timing is free of human think-time. Subsequent calls are ignored
 * so a post-login re-render cannot retroactively mark the load as clean.
 */
export const setInitialAuthStatus = (hadSessionAtStart: boolean): void => {
  if (initialized) return;
  initialized = true;
  manualLogin = !hadSessionAtStart;
};

/**
 * Marks the current page load as having required a manual login — used when a
 * stale/invalid session forces a logout → /auth redirect even though a session
 * appeared present at cold load.
 */
export const markManualLoginRequired = (): void => {
  manualLogin = true;
};

/**
 * Records the end-to-end `app_total_load` Datadog RUM duration vital once, for the
 * first plugin that mounts (the landing route). Measures wall-clock time from
 * document start (`window.timeEvents.fieldView.appStart`, set in index.html) until
 * the first plugin bundle mounts. The `manualLogin` context flag lets dashboards
 * exclude human login think-time by filtering to `manualLogin:false`. Gated on an
 * available RUM instance and a valid appStart anchor.
 */
export const recordAppTotalLoadOnce = (landingPlugin: string): void => {
  if (hasRecorded) return;
  // Wait for the route's content plugin; skip shell plugins without consuming
  // the once-guard so a later content plugin can still close the timer.
  if (SHELL_PLUGINS.has(landingPlugin)) return;
  const appStart = window.timeEvents?.fieldView?.appStart;
  if (!appStart) return;
  const duration = Date.now() - appStart;
  if (duration <= 0 || duration > MAX_PLAUSIBLE_LOAD_MS) return;
  hasRecorded = true;
  DD.addDurationVital(APP_TOTAL_LOAD_VITAL, {
    startTime: appStart,
    duration,
    context: { landingPlugin, manualLogin },
    description:
      'End-to-end wall-clock time from document start until the first plugin bundle mounts',
  });
};

/** Test-only reset of this module's singleton state. */
export const resetAppTotalLoadForTests = (): void => {
  hasRecorded = false;
  initialized = false;
  manualLogin = true;
};
